import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { getAllEmailAddresses, updateBlockStatus, sendBulkEmail, updateEmail, createBulkEmailAddresses, exportEmailAddresses } from "../../services/EmailService";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, convertToRaw, ContentState } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { getAllEmailTemplates } from '../../services/TemplatesService';
import Pagination from "../../utils/Pagination";
import ExportButton from "../ExportButton";

const EmailAddresses = () => {
  const { customerId } = useParams();
  const [emailAddresses, setEmailAddresses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [selectedEmailAddress, setSelectedEmailAddress] = useState(null);
  const [blockReason, setBlockReason] = useState("");
  const [selectedEmails, setSelectedEmails] = useState(new Set());
  const [editSidebarOpen, setEditSidebarOpen] = useState(false);
  const [editingEmail, setEditingEmail] = useState(null);
  const [newEmailModalOpen, setNewEmailModalOpen] = useState(false);
  const [showCreateDropdown, setShowCreateDropdown] = useState(false);
  const [newEmail, setNewEmail] = useState({ address: '', fullName: '', metadata: '' });
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const fileInputRef = useRef(null);
  
  // New email form state
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [status, setStatus] = useState('');
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedEmailTemplateId, setSelectedEmailTemplateId] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  
  // Sort state
  const [sortBy, setSortBy] = useState('address');
  const [sortDescending, setSortDescending] = useState(false);

  // Filter sidebar state
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState({
    searchTerm: '',
    fullName: '',
    emailAddresses: '',
    isBlocked: '',
    isSubscribed: '',
    blockReason: '',
    metadata: '',
    blockedFrom: '',
    blockedTo: '',
  });
  const [appliedFilters, setAppliedFilters] = useState({
    searchTerm: '',
    fullName: '',
    emailAddresses: '',
    isBlocked: '',
    isSubscribed: '',
    blockReason: '',
    metadata: '',
    blockedFrom: '',
    blockedTo: '',
  });

  // Sync tempFilters when sidebar opens or appliedFilters change
  useEffect(() => {
    setTempFilters({ ...appliedFilters });
  }, [isFilterOpen, appliedFilters]);

  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState(null);

  const [csvPreviewData, setCsvPreviewData] = useState([]);
  const [csvPreviewModalOpen, setCsvPreviewModalOpen] = useState(false);
  const [isSavingBulk, setIsSavingBulk] = useState(false);

  // Count active filters
  const activeFilterCount = Object.values(appliedFilters).filter(v => v !== '').length;

  const fetchEmailAddresses = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = {
        pageNumber: currentPage,
        pageSize,
        searchTerm: appliedFilters.searchTerm || undefined,
        sortBy,
        sortDescending,
      };

      if (appliedFilters.fullName) params.fullName = appliedFilters.fullName;
      if (appliedFilters.emailAddresses) params.emailAddresses = appliedFilters.emailAddresses;
      if (appliedFilters.isBlocked !== '') params.isBlocked = appliedFilters.isBlocked === 'true';
      if (appliedFilters.isSubscribed !== '') params.isSubscribed = appliedFilters.isSubscribed === 'true';
      if (appliedFilters.blockReason) params.blockReason = appliedFilters.blockReason;
      if (appliedFilters.metadata) params.metadata = appliedFilters.metadata;
      if (appliedFilters.blockedFrom) params.blockedFrom = appliedFilters.blockedFrom;
      if (appliedFilters.blockedTo) params.blockedTo = appliedFilters.blockedTo;

      const data = await getAllEmailAddresses(params);
      setEmailAddresses(data.items || []);
      setTotalItems(data.totalCount || 0);
    } catch (err) {
      setError("Failed to load email addresses");
      setEmailAddresses([]);
      setTotalItems(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, sortBy, sortDescending, appliedFilters]);

  useEffect(() => {
    fetchEmailAddresses();
  }, [fetchEmailAddresses]);

  useEffect(() => {
    async function fetchEmailTemplates() {
      try {
        const data = await getAllEmailTemplates();
        setEmailTemplates(data);
      } catch (err) {
        toast.error('Failed to load templates');
      }
    }
    if (newEmailModalOpen) {
      fetchEmailTemplates();
    }
  }, [newEmailModalOpen]);

  // Filter handlers
  const handleTempFilterChange = (e) => {
    const { name, value } = e.target;
    setTempFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...tempFilters });
    setCurrentPage(1);
    setIsFilterOpen(false);
  };

  const handleClearAllFilters = () => {
    const emptyFilters = {
      searchTerm: '',
      fullName: '',
      emailAddresses: '',
      isBlocked: '',
      isSubscribed: '',
      blockReason: '',
      metadata: '',
      blockedFrom: '',
      blockedTo: '',
    };
    setTempFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setCurrentPage(1);
  };

  const handleExport = async (format) => {
    setIsExporting(true);
    try {
      const params = {
        sortBy,
        sortDescending,
        exportTo: format,
      };
      if (appliedFilters.searchTerm) params.searchTerm = appliedFilters.searchTerm;
      if (appliedFilters.fullName) params.fullName = appliedFilters.fullName;
      if (appliedFilters.emailAddresses) params.emailAddresses = appliedFilters.emailAddresses;
      if (appliedFilters.isBlocked !== '') params.isBlocked = appliedFilters.isBlocked === 'true';
      if (appliedFilters.isSubscribed !== '') params.isSubscribed = appliedFilters.isSubscribed === 'true';
      if (appliedFilters.blockReason) params.blockReason = appliedFilters.blockReason;
      if (appliedFilters.metadata) params.metadata = appliedFilters.metadata;
      if (appliedFilters.blockedFrom) params.blockedFrom = appliedFilters.blockedFrom;
      if (appliedFilters.blockedTo) params.blockedTo = appliedFilters.blockedTo;

      const response = await exportEmailAddresses(params);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `email-addresses.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to export email addresses");
    } finally {
      setIsExporting(false);
    }
  };

  const handleTemplateChange = (e) => {
    const templateId = e.target.value;
    setSelectedEmailTemplateId(templateId);
    const template = emailTemplates.find(t => String(t.id) === String(templateId));
    if (template) {
      setSubject(template.subject || '');
      if (template.body) {
        const contentBlock = htmlToDraft(template.body);
        if (contentBlock) {
          const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
          setEditorState(EditorState.createWithContent(contentState));
        }
      } else {
        setEditorState(EditorState.createEmpty());
      }
    } else {
      setSubject('');
      setEditorState(EditorState.createEmpty());
    }
  };

  const handleNewEmailSubmit = async (e) => {
    e.preventDefault();
    setStatus('Sending...');
    try {
      const body = draftToHtml(convertToRaw(editorState.getCurrentContent()));
      const emailList = to.split(',').map(email => email.trim()).filter(Boolean);
      await sendBulkEmail({ 
        to: emailList, 
        subject, 
        body, 
        EmailTemplateId: selectedEmailTemplateId ? Number(selectedEmailTemplateId) : undefined 
      });
      setStatus('Email sent successfully!');
      setTo('');
      setSubject('');
      setEditorState(EditorState.createEmpty());
      setSelectedEmailTemplateId('');
      setSelectedEmails(new Set());
      toast.success('Email sent successfully!', { position: 'top-right', autoClose: 2500 });
      setNewEmailModalOpen(false);
    } catch (error) {
      setStatus('Failed to send email.');
      toast.error('Failed to send email.', { position: 'top-right', autoClose: 2500 });
    }
  };

  const handleEmailSelect = (email) => {
    const newSelected = new Set(selectedEmails);
    if (newSelected.has(email.address)) {
      newSelected.delete(email.address);
    } else {
      newSelected.add(email.address);
    }
    setSelectedEmails(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedEmails.size === emailAddresses.length) {
      setSelectedEmails(new Set());
    } else {
      setSelectedEmails(new Set(emailAddresses.map(email => email.address)));
    }
  };

  const handleEditEmail = (email) => {
    setEditingEmail(email);
    setEditSidebarOpen(true);
  };

  const handleSendToSelected = () => {
    setTo(Array.from(selectedEmails).join(', '));
    setNewEmailModalOpen(true);
  };

  const handleBlockEmailAddress = async (address, shouldBlock) => {
    try {
      await updateBlockStatus({
        email: address,
        shouldBlock,
        reason: blockReason
      });
      toast.success(`Email address ${shouldBlock ? 'blocked' : 'unblocked'} successfully`);
      setBlockModalOpen(false);
      setBlockReason("");
      setSelectedEmailAddress(null);
      fetchEmailAddresses();
    } catch (error) {
      toast.error(`Failed to ${shouldBlock ? 'block' : 'unblock'} email address`);
    }
  };

  const openBlockModal = (address, isBlocked) => {
    setSelectedEmailAddress({ address, isBlocked });
    setBlockModalOpen(true);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDescending(!sortDescending);
    } else {
      setSortBy(column);
      setSortDescending(false);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(totalItems / pageSize);

  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    setIsUpdating(true);
    setUpdateError(null);

    try {
      await updateEmail({
        id: editingEmail.id,
        fullName: editingEmail.fullName,
        metadata: editingEmail.metadata
      });

      toast.success('Email updated successfully');
      setEditSidebarOpen(false);
      fetchEmailAddresses();
    } catch (error) {
      setUpdateError('Failed to update email. Please try again.');
      toast.error('Failed to update email');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCreateEmail = async (e) => {
    e.preventDefault();
    setIsCreating(true);
    setCreateError(null);

    try {
      await createBulkEmailAddresses([{
        address: newEmail.address,
        fullName: newEmail.fullName,
        metadata: newEmail.metadata
      }]);

      toast.success('Email address created successfully');
      setNewEmailModalOpen(false);
      setNewEmail({ address: '', fullName: '', metadata: '' });
      fetchEmailAddresses();
    } catch (error) {
      setCreateError('Failed to create email address. Please try again.');
      toast.error('Failed to create email address');
    } finally {
      setIsCreating(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const csvData = event.target.result;
        const lines = csvData.split('\n');
        const emailAddresses = lines
          .filter(line => line.trim())
          .map(line => {
            const [fullName, address, metadata] = line.split(',').map(item => item.trim());
            return {
              address,
              fullName: fullName || '',
              metadata: metadata || ''
            };
          });

        setCsvPreviewData(emailAddresses);
        setCsvPreviewModalOpen(true);
      } catch (error) {
        toast.error('Failed to parse CSV file');
      }
    };
    reader.readAsText(file);
  };

  const handleCsvDataChange = (index, field, value) => {
    const newData = [...csvPreviewData];
    newData[index] = {
      ...newData[index],
      [field]: value
    };
    setCsvPreviewData(newData);
  };

  const handleSaveBulkEmails = async () => {
    setIsSavingBulk(true);
    try {
      await createBulkEmailAddresses(csvPreviewData);
      toast.success(`${csvPreviewData.length} email addresses created successfully`);
      setCsvPreviewModalOpen(false);
      setCsvPreviewData([]);
      fetchEmailAddresses();
    } catch (error) {
      toast.error('Failed to create email addresses');
    } finally {
      setIsSavingBulk(false);
    }
  };

  const handleRemoveRow = (index) => {
    setCsvPreviewData(csvPreviewData.filter((_, i) => i !== index));
  };

  if (isLoading && emailAddresses.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error && emailAddresses.length === 0) {
    return (
      <div className="alert alert-danger m-4" role="alert">
        {error}
      </div>
    );
  }

  return (
    <div className="container-fluid p-4">
      <ToastContainer />
      
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="fw-bold mb-1">Email Addresses</h3>
          <p className="text-muted mb-0">Manage and monitor email addresses</p>
        </div>
        <div className="d-flex gap-2 align-items-start">
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setIsFilterOpen(true)}
            disabled={isLoading}
          >
            <i className="bi bi-funnel-fill me-1"></i>
            Filter
            {activeFilterCount > 0 && (
              <span className="badge bg-primary ms-1">{activeFilterCount}</span>
            )}
          </button>
          <ExportButton onExport={handleExport} isExporting={isExporting} size="sm" />
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={fetchEmailAddresses}
            disabled={isLoading}
          >
            <i className={`${isLoading ? 'spinner-border spinner-border-sm' : 'bi bi-arrow-clockwise'} me-1`}></i>
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          {error}
        </div>
      )}

      {/* ==================== FILTER SIDEBAR ==================== */}
      {/* Overlay */}
      <div
        className={`position-fixed top-0 end-0 bottom-0 start-0 bg-dark bg-opacity-50 ${!isFilterOpen ? "d-none" : ""}`}
        onClick={() => setIsFilterOpen(false)}
        style={{ zIndex: 1040 }}
      />

      {/* Sidebar */}
      <div
        className={`position-fixed top-0 end-0 bottom-0 bg-white shadow overflow-auto ${!isFilterOpen ? "translate-end d-none" : ""}`}
        style={{ width: "500px", zIndex: 1045 }}
      >
        <div className="d-flex flex-column h-100">
          {/* Header */}
          <div className="p-3 border-bottom bg-light">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0 text-primary">
                <i className="bi bi-funnel-fill me-2"></i>Filter Email Addresses
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setIsFilterOpen(false)}
                aria-label="Close"
              />
            </div>
          </div>

          {/* Filter Fields */}
          <div className="p-3 flex-grow-1">
            {/* Search */}
            <div className="mb-4">
              <label className="form-label fw-bold small text-muted">Email Addresses</label>
              <div className="input-group">
                <span className="input-group-text"><i className="bi bi-search"></i></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search email addresses..."
                  name="emailAddresses"
                  value={tempFilters.emailAddresses}
                  onChange={handleTempFilterChange}
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Full Name */}
            <div className="mb-4">
              <label className="form-label fw-bold small text-muted">Full Name</label>
              <div className="input-group">
                <span className="input-group-text"><i className="bi bi-search"></i></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by full name..."
                  name="fullName"
                  value={tempFilters.fullName}
                  onChange={handleTempFilterChange}
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Metadata */}
            <div className="mb-4">
              <label className="form-label fw-bold small text-muted">Metadata</label>
              <div className="input-group">
                <span className="input-group-text"><i className="bi bi-search"></i></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by metadata..."
                  name="metadata"
                  value={tempFilters.metadata}
                  onChange={handleTempFilterChange}
                  disabled={isLoading}
                />
              </div>
            </div>

            <hr className="my-4" />

            <div className="vstack gap-4">
              {/* Status */}
              <div>
                <label className="form-label fw-bold small text-muted mb-2">Status</label>
                <div className="row g-2">
                  <div className="col-6">
                    <label htmlFor="isBlocked" className="form-label small text-muted d-block">Block Status</label>
                    <select
                      className="form-select"
                      id="isBlocked"
                      name="isBlocked"
                      value={tempFilters.isBlocked}
                      onChange={handleTempFilterChange}
                      disabled={isLoading}
                    >
                      <option value="">All</option>
                      <option value="false">Active</option>
                      <option value="true">Blocked</option>
                    </select>
                  </div>
                  <div className="col-6">
                    <label htmlFor="isSubscribed" className="form-label small text-muted d-block">Subscription Status</label>
                    <select
                      className="form-select"
                      id="isSubscribed"
                      name="isSubscribed"
                      value={tempFilters.isSubscribed}
                      onChange={handleTempFilterChange}
                      disabled={isLoading}
                    >
                      <option value="">All</option>
                      <option value="true">Subscribed</option>
                      <option value="false">Not Subscribed</option>
                    </select>
                  </div>
                </div>
              </div>

             {/* Block Details */}
              <div>
                <label className="form-label fw-bold small text-muted mb-2">Block Details</label>
                <div className="mb-3">
                  <label htmlFor="filterBlockReason" className="form-label small text-muted d-block">Block Reason</label>
                  <div className="input-group">
                    <span className="input-group-text"><i className="bi bi-search"></i></span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by block reason..."
                      id="filterBlockReason"
                      name="blockReason"
                      value={tempFilters.blockReason}
                      onChange={handleTempFilterChange}
                      disabled={isLoading}
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label small text-muted">Blocked From</label>
                  <div className="input-group">
                    <span className="input-group-text"><i className="bi bi-calendar-event"></i></span>
                    <input
                      type="date"
                      className="form-control"
                      name="blockedFrom"
                      value={tempFilters.blockedFrom}
                      onChange={handleTempFilterChange}
                      disabled={isLoading}
                    />
                  </div>
                </div>
                <div>
                  <label className="form-label small text-muted">Blocked To</label>
                  <div className="input-group">
                    <span className="input-group-text"><i className="bi bi-calendar-event"></i></span>
                    <input
                      type="date"
                      className="form-control"
                      name="blockedTo"
                      value={tempFilters.blockedTo}
                      onChange={handleTempFilterChange}
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-3 border-top bg-light">
            <div className="d-grid gap-2">
              <button
                className="btn btn-outline-secondary"
                onClick={handleClearAllFilters}
                disabled={isLoading}
              >
                <i className="bi bi-x-circle me-1"></i>Clear All Filters
              </button>
              <button
                className="btn btn-primary"
                onClick={handleApplyFilters}
                disabled={isLoading}
              >
                <i className="bi bi-search me-1"></i>Search / Apply Filters
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* ==================== END FILTER SIDEBAR ==================== */}

      {emailAddresses.length === 0 && !isLoading ? (
        <div className="alert alert-info">
          No email addresses found.
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <table className="table table-striped table-hover">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={selectedEmails.size === emailAddresses.length && emailAddresses.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th>
                    <button 
                      className="btn btn-link text-decoration-none text-dark p-0"
                      onClick={() => handleSort('fullName')}
                    >
                      Full Name {sortBy === 'fullName' && (sortDescending ? '↓' : '↑')}
                    </button>
                  </th>
                  <th>
                    <button 
                      className="btn btn-link text-decoration-none text-dark p-0"
                      onClick={() => handleSort('address')}
                    >
                      Email Address {sortBy === 'address' && (sortDescending ? '↓' : '↑')}
                    </button>
                  </th>
                  <th>
                    <button 
                      className="btn btn-link text-decoration-none text-dark p-0"
                      onClick={() => handleSort('isblocked')}
                    >
                      Status {sortBy === 'isblocked' && (sortDescending ? '↓' : '↑')}
                    </button>
                  </th>
                  <th>Subscription status</th>
                  <th>Block Reason</th>
                  <th>
                    <button 
                      className="btn btn-link text-decoration-none text-dark p-0"
                      onClick={() => handleSort('blockedAt')}
                    >
                      Blocked At {sortBy === 'blockedAt' && (sortDescending ? '↓' : '↑')}
                    </button>
                  </th>
                  <th>Metadata</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {emailAddresses.map((email) => (
                  <tr key={email.address}>
                    <td>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedEmails.has(email.address)}
                        onChange={() => handleEmailSelect(email)}
                      />
                    </td>
                    <td>{email.fullName || '-'}</td>
                    <td>{email.address}</td>
                    <td>
                      <span className={`badge bg-${email.isBlocked ? 'danger' : 'success'}`}>
                        {email.isBlocked ? 'Blocked' : 'Active'}
                      </span>
                    </td>
                    <td>
                      <span className={`badge bg-${email.isSubscribed ? 'success' : 'danger'}`}>
                        {email.isSubscribed ? 'Subscribed' : 'Not Subscribed'}
                      </span>
                    </td>
                    <td>{email?.blockReason || '-'}</td>
                    <td>
                      {email.blockedAt
                        ? new Date(email.blockedAt).toLocaleString()
                        : '-'}
                    </td>
                    <td>{email.metadata || '-'}</td>
                    <td>
                      <div className="btn-group">
                        <button
                          className="btn btn-sm btn-outline-primary me-2"
                          onClick={() => handleEditEmail(email)}
                          title="Edit Email Address"
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => openBlockModal(email.address, email.isBlocked)}
                          title={email.isBlocked ? `Blocked: ${email.blockedReason || 'No reason provided'}` : "Block Email Address"}
                        >
                          <i className={`bi bi-${email.isBlocked ? 'unlock' : 'lock'}`}></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination Controls */}
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="d-flex align-items-center">
              <select 
                className="form-select form-select-sm me-2" 
                value={pageSize} 
                onChange={handlePageSizeChange}
                style={{ width: 'auto' }}
              >
                <option value={5}>5 per page</option>
                <option value={10}>10 per page</option>
                <option value={20}>20 per page</option>
                <option value={50}>50 per page</option>
              </select>
              <span className="text-muted">
                Showing {totalItems === 0 ? 0 : ((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalItems)} of {totalItems} entries
              </span>
            </div>
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
          </div>
        </>
      )}

      {/* Floating Action Buttons */}
      <div className="position-fixed" style={{ bottom: '2rem', right: '2rem', zIndex: 1040 }}>
        {selectedEmails.size > 0 && (
          <button
            className="btn btn-primary rounded-circle mb-2"
            style={{
              width: '3.5rem',
              height: '3.5rem',
              boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
            }}
            onClick={handleSendToSelected}
            title={`Send email to ${selectedEmails.size} selected addresses`}
          >
            <i className="bi bi-envelope-plus fs-4"></i>
          </button>
        )}
        
        <div className="position-relative">
          <button
            className="btn btn-primary rounded-circle"
            style={{
              width: '3.5rem',
              height: '3.5rem',
              boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
            }}
            onClick={() => setShowCreateDropdown(!showCreateDropdown)}
            title="Create new email address"
          >
            <i className="bi bi-plus-lg fs-4"></i>
          </button>
          
          {showCreateDropdown && (
            <div 
              className="position-absolute bottom-100 end-0 mb-2 bg-white rounded shadow-lg"
              style={{ width: '300px' }}
            >
              <div className="p-2">
                <button
                  className="btn btn-light w-100 text-start mb-2"
                  onClick={() => {
                    setShowCreateDropdown(false);
                    setNewEmailModalOpen(true);
                  }}
                >
                  <i className="bi bi-person-plus me-2"></i>
                  Create new email address
                </button>
                <button
                  className="btn btn-light w-100 text-start"
                  onClick={() => {
                    setShowCreateDropdown(false);
                    fileInputRef.current?.click();
                  }}
                >
                  <i className="bi bi-upload me-2"></i>
                  Upload email addresses
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit Sidebar */}
      {editSidebarOpen && editingEmail && (
        <div className="position-fixed top-0 end-0 bottom-0 bg-white shadow overflow-auto" style={{ width: '400px', zIndex: 1045 }}>
          <div className="p-3 border-bottom d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Edit Email Address</h5>
            <button type="button" className="btn-close" onClick={() => setEditSidebarOpen(false)}></button>
          </div>
          <div className="p-3">
            <form onSubmit={handleUpdateEmail}>
              <div className="mb-3">
                <label className="form-label fw-semibold">Full Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={editingEmail.fullName || ''}
                  onChange={(e) => setEditingEmail({ ...editingEmail, fullName: e.target.value })}
                />
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold">Email Address</label>
                <input
                  type="email"
                  className="form-control"
                  value={editingEmail.address}
                  disabled
                />
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold">Metadata</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={editingEmail.metadata || ''}
                  onChange={(e) => setEditingEmail({ ...editingEmail, metadata: e.target.value })}
                />
              </div>
              {updateError && (
                <div className="alert alert-danger mb-3">
                  {updateError}
                </div>
              )}
              <div className="d-flex justify-content-end gap-2">
                <button 
                  type="button" 
                  className="btn btn-outline-secondary" 
                  onClick={() => setEditSidebarOpen(false)}
                  disabled={isUpdating}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={isUpdating}
                >
                  {isUpdating ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New Email Address Modal */}
      {newEmailModalOpen && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Create New Email Address</h5>
                <button type="button" className="btn-close" onClick={() => setNewEmailModalOpen(false)}></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleCreateEmail}>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Email Address *</label>
                    <input
                      type="email"
                      className="form-control"
                      value={newEmail.address}
                      onChange={(e) => setNewEmail({ ...newEmail, address: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Full Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={newEmail.fullName}
                      onChange={(e) => setNewEmail({ ...newEmail, fullName: e.target.value })}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Metadata</label>
                    <textarea
                      className="form-control"
                      rows={3}
                      value={newEmail.metadata}
                      onChange={(e) => setNewEmail({ ...newEmail, metadata: e.target.value })}
                    />
                  </div>
                  {createError && (
                    <div className="alert alert-danger mb-3">
                      {createError}
                    </div>
                  )}
                  <div className="d-flex justify-content-end gap-2">
                    <button 
                      type="button" 
                      className="btn btn-outline-secondary" 
                      onClick={() => setNewEmailModalOpen(false)}
                      disabled={isCreating}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="btn btn-primary"
                      disabled={isCreating}
                    >
                      {isCreating ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Creating...
                        </>
                      ) : (
                        'Create Email Address'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSV Preview Modal */}
      {csvPreviewModalOpen && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Preview Email Addresses</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => {
                    setCsvPreviewModalOpen(false);
                    setCsvPreviewData([]);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info mb-3">
                  <i className="bi bi-info-circle me-2"></i>
                  Review and edit the email addresses before saving. You can remove rows by clicking the delete button.
                </div>
                <div className="table-responsive" style={{ maxHeight: '60vh' }}>
                  <table className="table table-hover">
                    <thead className="sticky-top bg-white">
                      <tr>
                        <th>Full Name</th>
                        <th>Email Address</th>
                        <th>Metadata</th>
                        <th style={{ width: '50px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {csvPreviewData.map((row, index) => (
                        <tr key={index}>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={row.fullName}
                              onChange={(e) => handleCsvDataChange(index, 'fullName', e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type="email"
                              className="form-control form-control-sm"
                              value={row.address}
                              onChange={(e) => handleCsvDataChange(index, 'address', e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={row.metadata}
                              onChange={(e) => handleCsvDataChange(index, 'metadata', e.target.value)}
                            />
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleRemoveRow(index)}
                              title="Remove row"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {csvPreviewData.length === 0 && (
                  <div className="text-center text-muted py-4">
                    No email addresses to preview
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    setCsvPreviewModalOpen(false);
                    setCsvPreviewData([]);
                  }}
                  disabled={isSavingBulk}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveBulkEmails}
                  disabled={isSavingBulk || csvPreviewData.length === 0}
                >
                  {isSavingBulk ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-save me-2"></i>
                      Save {csvPreviewData.length} Email Addresses
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hidden file input for CSV upload */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept=".csv"
        onChange={handleFileUpload}
      />

      {/* Block/Unblock Modal */}
      {blockModalOpen && selectedEmailAddress && (
        <div className="modal fade show" style={{ display: 'block' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {selectedEmailAddress.isBlocked ? 'Unblock Email Address' : 'Block Email Address'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setBlockModalOpen(false);
                    setSelectedEmailAddress(null);
                    setBlockReason("");
                  }}
                ></button>
              </div>
              <div className="modal-body">
                {!selectedEmailAddress.isBlocked && (
                  <div className="mb-3">
                    <label htmlFor="blockReasonInput" className="form-label">Reason for blocking</label>
                    <textarea
                      className="form-control"
                      id="blockReasonInput"
                      rows={3}
                      value={blockReason}
                      onChange={(e) => setBlockReason(e.target.value)}
                      placeholder="Enter reason for blocking this email address"
                      required
                    />
                  </div>
                )}
                <p>
                  Are you sure you want to {selectedEmailAddress.isBlocked ? 'unblock' : 'block'} the email address{' '}
                  <strong>{selectedEmailAddress.address}</strong>?
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setBlockModalOpen(false);
                    setSelectedEmailAddress(null);
                    setBlockReason("");
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={`btn btn-${selectedEmailAddress.isBlocked ? 'success' : 'danger'}`}
                  onClick={() => handleBlockEmailAddress(
                    selectedEmailAddress.address,
                    !selectedEmailAddress.isBlocked
                  )}
                  disabled={!selectedEmailAddress.isBlocked && !blockReason.trim()}
                >
                  {selectedEmailAddress.isBlocked ? 'Unblock' : 'Block'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailAddresses;